import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { errorText } from '../../../lib/error-text';
import {
  assignmentApi,
  quizReviewApi,
  type QuizReviewDto,
  type QuizReviewQuestionDto,
} from '../../../lib/api/assignment.api';

/** Teacher review of a student's quiz attempt. Closed questions are read-only
 *  (student vs correct + score). Open questions are interactive: the teacher can
 *  generate an AI proposal (score + feedback against the standard), adjust the
 *  per-question score, and consolidate the total grade to the gradebook. Staff-only. */
export function QuizReviewPanel({
  tenantId,
  assignmentId,
  submissionId,
  studentName,
  onLoaded,
}: {
  tenantId: string;
  assignmentId: string;
  submissionId: string;
  studentName: string;
  /** Reports whether the quiz has open questions once the review loads, so the
   *  parent can hide the redundant generic grade surface (consolidation lives here). */
  onLoaded?: (info: { hasOpen: boolean }) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const [review, setReview] = useState<QuizReviewDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [consolidating, setConsolidating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  /** Teacher-set awarded points per open question id. */
  const [openScores, setOpenScores] = useState<Record<string, number>>({});

  const seedScores = useCallback((r: QuizReviewDto) => {
    const next: Record<string, number> = {};
    for (const q of r.questions) {
      if (q.question.kind !== 'open') continue;
      // Seed from the AI proposal (suggestedScore is 0..1 × max) when present,
      // otherwise from any awarded value, otherwise 0.
      const fromAi =
        q.aiProposal?.suggestedScore !== undefined
          ? round(q.aiProposal.suggestedScore * q.max)
          : undefined;
      next[q.question.id] = fromAi ?? q.awarded ?? 0;
    }
    setOpenScores(next);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    return quizReviewApi
      .get({ tenantId, assignmentId, submissionId })
      .then((r) => {
        setReview(r);
        seedScores(r);
        onLoaded?.({ hasOpen: r.hasOpen });
      })
      .catch((e) => setErr(errorText(e)))
      .finally(() => setLoading(false));
  }, [tenantId, assignmentId, submissionId, seedScores, onLoaded]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    quizReviewApi
      .get({ tenantId, assignmentId, submissionId })
      .then((r) => {
        if (cancelled) return;
        setReview(r);
        seedScores(r);
        onLoaded?.({ hasOpen: r.hasOpen });
      })
      .catch((e) => {
        if (!cancelled) setErr(errorText(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, assignmentId, submissionId, seedScores, onLoaded]);

  const generate = async (force: boolean) => {
    setGenerating(true);
    setErr(null);
    setNotice(null);
    try {
      await assignmentApi.generateReview({ tenantId, submissionId, force });
      await load();
      setNotice(t('assignmentsAdmin.quizReview.suggestionReady'));
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setGenerating(false);
    }
  };

  const openMax = review?.openMaxPoints ?? 0;
  const openAwarded = useMemo(
    () => Object.values(openScores).reduce((s, n) => s + (Number.isFinite(n) ? n : 0), 0),
    [openScores],
  );
  const total = round((review?.closedRawPoints ?? 0) + openAwarded);
  const grandMax = round((review?.maxPoints ?? 0) + openMax);

  const consolidate = async () => {
    if (!review) return;
    setConsolidating(true);
    setErr(null);
    setNotice(null);
    try {
      await assignmentApi.grade({
        tenantId,
        submissionId,
        rawPoints: total,
        ...(review.review?.id ? { reviewId: review.review.id } : {}),
      });
      await load();
      setNotice(t('assignmentsAdmin.quizReview.consolidated'));
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setConsolidating(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        mr: { md: 1 },
        position: { md: 'sticky' },
        top: 16,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <PersonRounded fontSize="small" color="primary" />
        <Typography variant="subtitle1" fontWeight={700} color="primary" noWrap sx={{ flex: 1 }}>
          {studentName}
        </Typography>
        {review && (
          <Chip
            size="small"
            color="primary"
            label={t('assignmentsAdmin.quizReview.score', {
              raw: round(review.hasOpen ? total : review.rawPoints),
              max: round(review.hasOpen ? grandMax : review.maxPoints),
            })}
          />
        )}
      </Stack>

      <Box sx={{ p: 2 }}>
        {loading ? (
          <Box textAlign="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : err ? (
          <Alert severity="error">{err}</Alert>
        ) : review ? (
          <Stack spacing={2.5}>
            {notice && (
              <Alert severity="success" onClose={() => setNotice(null)}>
                {notice}
              </Alert>
            )}
            {review.questions.map((rq, idx) =>
              rq.question.kind === 'open' ? (
                <OpenQuestionReview
                  key={rq.question.id}
                  rq={rq}
                  index={idx}
                  score={openScores[rq.question.id] ?? 0}
                  onScore={(n) => setOpenScores((prev) => ({ ...prev, [rq.question.id]: n }))}
                />
              ) : (
                <QuestionReview key={rq.question.id} rq={rq} index={idx} />
              ),
            )}

            {review.hasOpen && (
              <>
                <Divider />
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        generating ? <CircularProgress size={14} /> : <AutoAwesomeRoundedIcon />
                      }
                      disabled={generating || consolidating}
                      onClick={() => generate(!!review.review)}
                    >
                      {review.review
                        ? t('assignmentsAdmin.quizReview.regenerateSuggestion')
                        : t('assignmentsAdmin.quizReview.generateSuggestion')}
                    </Button>
                    {review.review && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t('assignmentsAdmin.quizReview.cost', {
                          usd: review.review.costEstimateUsd.toFixed(2),
                        })}
                      />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {t('assignmentsAdmin.quizReview.suggestionDisclaimer')}
                  </Typography>

                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ pt: 0.5 }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      {t('assignmentsAdmin.quizReview.total', {
                        raw: round(total),
                        max: round(grandMax),
                      })}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={consolidating || generating}
                      startIcon={consolidating ? <CircularProgress size={14} /> : undefined}
                      onClick={consolidate}
                    >
                      {t('assignmentsAdmin.quizReview.consolidate')}
                    </Button>
                  </Stack>
                  {review.grade && (
                    <Typography variant="caption" color="success.main">
                      {t('assignmentsAdmin.quizReview.currentGrade', {
                        pts: round(review.grade.finalPoints),
                      })}
                    </Typography>
                  )}
                </Stack>
              </>
            )}
          </Stack>
        ) : null}
      </Box>
    </Paper>
  );
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

// ─── Open question: interactive (AI proposal + editable score) ──────────────────

function OpenQuestionReview({
  rq,
  index,
  score,
  onScore,
}: {
  rq: QuizReviewQuestionDto;
  index: number;
  score: number;
  onScore: (n: number) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const promptText = typeof rq.question.prompt === 'string' ? rq.question.prompt : '';
  const studentText = rq.studentAnswer?.kind === 'open' ? rq.studentAnswer.text : '';

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={1}
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: (th) => alpha(th.palette.info.main, 0.06),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, lineHeight: 1.4 }}>
          {index + 1}. {promptText}
        </Typography>
        <Chip
          size="small"
          color="info"
          variant="outlined"
          label={t('assignmentsAdmin.quizReview.openChip')}
          sx={{ height: 22, flexShrink: 0 }}
        />
      </Stack>

      <Stack spacing={1.25} sx={{ p: 1.5 }}>
        {rq.standard && (
          <Field label={t('assignmentsAdmin.quizReview.expected')}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
              {rq.standard}
            </Typography>
          </Field>
        )}

        <Field label={t('assignmentsAdmin.quizReview.studentAnswer')}>
          <Box
            sx={{
              px: 1.25,
              py: 1,
              borderRadius: 1.5,
              bgcolor: (th) => alpha(th.palette.text.primary, 0.04),
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {studentText || '—'}
            </Typography>
          </Box>
        </Field>

        {rq.aiProposal && (
          <Box
            sx={{
              px: 1.25,
              py: 1,
              borderRadius: 1.5,
              bgcolor: (th) => alpha(th.palette.secondary.main, 0.08),
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.secondary.main, 0.25),
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center" mb={0.5} flexWrap="wrap">
              <AutoAwesomeRoundedIcon sx={{ fontSize: 16 }} color="secondary" />
              <Typography variant="caption" fontWeight={700} color="secondary.main">
                {t('assignmentsAdmin.quizReview.suggestedTitle')}
              </Typography>
              {rq.aiProposal.suggestedScore !== undefined && (
                <Chip
                  size="small"
                  color="secondary"
                  variant="outlined"
                  label={`${round(rq.aiProposal.suggestedScore * rq.max)}/${round(rq.max)}`}
                  sx={{ height: 20 }}
                />
              )}
              <Typography variant="caption" color="text.secondary">
                {t(`assignmentsAdmin.quizReview.confidence.${rq.aiProposal.confidence}`)}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {rq.aiProposal.feedback || '—'}
            </Typography>
          </Box>
        )}

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            type="number"
            label={t('assignmentsAdmin.quizReview.awardPoints')}
            value={Number.isFinite(score) ? score : 0}
            onChange={(e) => {
              const n = Number(e.target.value);
              onScore(Math.max(0, Math.min(rq.max, Number.isFinite(n) ? n : 0)));
            }}
            inputProps={{ min: 0, max: rq.max, step: 0.5 }}
            sx={{ width: 130 }}
          />
          <Typography variant="body2" color="text.secondary">
            {t('assignmentsAdmin.quizReview.outOf', { max: round(rq.max) })}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

/** A stacked label + content block used inside the open-question card. */
function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', fontWeight: 600, mb: 0.25 }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

// ─── Closed question: read-only ─────────────────────────────────────────────────

function QuestionReview({ rq, index }: { rq: QuizReviewQuestionDto; index: number }): JSX.Element {
  const { t } = useTranslation('sis');
  const promptText = typeof rq.question.prompt === 'string' ? rq.question.prompt : '';
  const partial = !rq.deferred && rq.awarded > 0 && !rq.correct;

  const icon = rq.correct ? (
    <CheckCircleRoundedIcon fontSize="small" color="success" />
  ) : partial ? (
    <CheckCircleRoundedIcon fontSize="small" color="warning" />
  ) : (
    <CancelRoundedIcon fontSize="small" color="error" />
  );

  const tone = rq.correct ? 'success' : partial ? 'warning' : 'error';

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75}>
        {icon}
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
          {index + 1}. {promptText}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={`${round(rq.awarded)}/${round(rq.max)}`}
          sx={{ height: 22 }}
        />
      </Stack>

      <Stack spacing={0.5} sx={{ pl: 3.5 }}>
        <AnswerLine
          label={t('assignmentsAdmin.quizReview.studentAnswer')}
          value={renderStudent(rq)}
          tone={tone}
        />
        {!rq.correct && (
          <AnswerLine
            label={t('assignmentsAdmin.quizReview.correctAnswer')}
            value={renderCorrect(rq)}
            tone="success"
          />
        )}
      </Stack>
    </Box>
  );
}

function AnswerLine({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'success' | 'warning' | 'error' | 'info';
}): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        bgcolor: (th) => alpha(th.palette[tone].main, 0.1),
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

// ─── Per-kind renderers: turn ids into human-readable text ──────────────────────

function renderStudent(rq: QuizReviewQuestionDto): string {
  const q = rq.question;
  const a = rq.studentAnswer;
  switch (q.kind) {
    case 'single_choice':
      return a?.kind === 'single_choice' ? optText(q.options, a.selectedOptionId) : '';
    case 'multiple_choice':
      return a?.kind === 'multiple_choice'
        ? a.selectedOptionIds.map((id) => optText(q.options, id)).join(', ')
        : '';
    case 'matching':
      return a?.kind === 'matching'
        ? a.pairs
            .map((p) => `${optText(q.left, p.leftId)} → ${optText(q.right, p.rightId)}`)
            .join('\n')
        : '';
    case 'ordering':
      return a?.kind === 'ordering'
        ? a.orderedItemIds.map((id, i) => `${i + 1}. ${optText(q.items, id)}`).join('\n')
        : '';
    case 'numeric':
      return a?.kind === 'numeric' && a.value !== undefined
        ? `${a.value}${q.unit ? ` ${q.unit}` : ''}`
        : '';
    case 'short_answer':
      return a?.kind === 'short_answer' ? a.text : '';
    case 'cloze':
      return a?.kind === 'cloze'
        ? Object.entries(a.values)
            .map(([b, v]) => `[[${b}]] ${v}`)
            .join('  ')
        : '';
    default:
      return a?.kind === 'open' ? a.text : '';
  }
}

function renderCorrect(rq: QuizReviewQuestionDto): string {
  const q = rq.question;
  const k = rq.key;
  switch (q.kind) {
    case 'single_choice':
      return k?.kind === 'single_choice' ? optText(q.options, k.correctOptionId) : '';
    case 'multiple_choice':
      return k?.kind === 'multiple_choice'
        ? k.correctOptionIds.map((id) => optText(q.options, id)).join(', ')
        : '';
    case 'matching':
      return k?.kind === 'matching'
        ? k.correctPairs
            .map((p) => `${optText(q.left, p.leftId)} → ${optText(q.right, p.rightId)}`)
            .join('\n')
        : '';
    case 'ordering':
      return k?.kind === 'ordering'
        ? k.correctOrder.map((id, i) => `${i + 1}. ${optText(q.items, id)}`).join('\n')
        : '';
    case 'numeric':
      return k?.kind === 'numeric'
        ? `${k.value} (±${k.tolerance})${q.unit ? ` ${q.unit}` : ''}`
        : '';
    case 'short_answer':
      return k?.kind === 'short_answer' ? k.acceptedAnswers.join(' / ') : '';
    case 'cloze':
      return k?.kind === 'cloze'
        ? Object.entries(k.blanks)
            .map(([b, spec]) => `[[${b}]] ${spec.acceptedAnswers.join(' / ')}`)
            .join('  ')
        : '';
    default:
      return '';
  }
}

function optText(options: { id: string; text: string }[], id: string | undefined): string {
  if (!id) return '';
  return options.find((o) => o.id === id)?.text ?? id;
}
